import { BASE_PATH } from '@/constants/config';
import {
  WORK_EMAIL_VERIFICATION_RETURN_MAX_AGE_SEC,
  WORK_EMAIL_VERIFICATION_RETURN_PATH_KEY,
} from '@/constants/work-email-verification';
import { deleteCookie, getCookie, setBrowserCookie } from '@/utils/cookies';

const PARTICIPANTS_PREFIX = `${BASE_PATH}/participants/`;

/**
 * Stored path must live under participant routes. Accepts `/next/participants/...`
 * or, when the app is mounted under BASE_PATH but pathname omits it, `/participants/...`.
 */
export function sanitizeWorkEmailVerificationReturnPath(
  raw: string | null | undefined,
): string | undefined {
  if (!raw) return undefined;
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return undefined;
  }
  const trimmed = decoded.trim();
  if (
    trimmed.length > 2048 ||
    trimmed.includes('..') ||
    /\s/.test(trimmed) ||
    trimmed.includes('://')
  ) {
    return undefined;
  }
  if (!trimmed.startsWith('/')) return undefined;

  let path = trimmed;
  if (
    !path.startsWith(PARTICIPANTS_PREFIX) &&
    path.startsWith('/participants/')
  ) {
    path = `${BASE_PATH}${path}`;
  }
  if (!path.startsWith(PARTICIPANTS_PREFIX)) return undefined;
  return path;
}

export function setWorkEmailVerificationReturnPathCookie(
  returnPath: string,
): void {
  const safe = sanitizeWorkEmailVerificationReturnPath(returnPath);
  if (!safe) return;

  setBrowserCookie(WORK_EMAIL_VERIFICATION_RETURN_PATH_KEY, safe, {
    maxAgeSeconds: WORK_EMAIL_VERIFICATION_RETURN_MAX_AGE_SEC,
  });
}

export function getWorkEmailVerificationReturnPathCookie(): string | undefined {
  const raw = getCookie(WORK_EMAIL_VERIFICATION_RETURN_PATH_KEY);
  return sanitizeWorkEmailVerificationReturnPath(raw);
}

export function clearWorkEmailVerificationReturnPathCookie(): void {
  deleteCookie(WORK_EMAIL_VERIFICATION_RETURN_PATH_KEY);
}

/** Reads and clears the return-path cookie. */
export function consumeWorkEmailVerificationReturnPathCookie():
  | string
  | undefined {
  const path = getWorkEmailVerificationReturnPathCookie();
  if (path) {
    clearWorkEmailVerificationReturnPathCookie();
  }
  return path;
}
